// frontend/src/utils/policy/responsePolicyContract.js
//
// RESPONSE POLICY CONTRACT
// ------------------------
// This file defines the neutral contract between dAIsy's runtime and the
// response execution boundary. The runtime decides what response behavior is
// admissible; this layer only represents and validates that decision.
//
// IMPORTANT FUTURE API BOUNDARY:
// A remote tenant/domain policy service may eventually provide objects in this
// shape. Remote policy must never be accepted as arbitrary prompt text. It must
// be authenticated, schema-validated, scoped, ranked by authority, and merged
// with non-overridable core policy before reaching the validator.

export const RESPONSE_POLICY_SCHEMA_VERSION = '1.0';

export const RESPONSE_POLICY_DECISIONS = Object.freeze({
  ALLOW: 'ALLOW',
  TRANSFORM: 'TRANSFORM',
  REJECT: 'REJECT',
  REGENERATE: 'REGENERATE',
});

export const RESPONSE_POLICY_ACTIONS = Object.freeze({
  REMOVE_SENTENCE: 'REMOVE_SENTENCE',
  PRESERVE: 'PRESERVE',
});

export const RESPONSE_POLICY_SOURCES = Object.freeze({
  CORE: 'core',
  RUNTIME: 'runtime',
  TENANT: 'tenant',
  DOMAIN: 'domain',
  REMOTE_API: 'remote_api',
});

export function createResponsePolicy(input = {}) {
  const rules = input.rules && typeof input.rules === 'object' ? input.rules : {};

  return {
    schemaVersion: RESPONSE_POLICY_SCHEMA_VERSION,
    policyId: String(input.policyId || 'daisy.response.runtime'),
    version: String(input.version || '1.0.0'),
    source: String(input.source || RESPONSE_POLICY_SOURCES.RUNTIME),
    authority: String(input.authority || 'runtime'),
    scope: String(input.scope || 'response_output'),
    priority: Number.isFinite(Number(input.priority)) ? Number(input.priority) : 100,
    provenance: input.provenance || null,
    mode: String(input.mode || 'normal'),
    responseIntent: String(input.responseIntent || 'ANSWER_NORMALLY'),
    rules: {
      allowOptionalQuestion:
        typeof rules.allowOptionalQuestion === 'boolean'
          ? rules.allowOptionalQuestion
          : null,
      questionAuthority: String(rules.questionAuthority || 'optional'),
      requireReasoningAdvance: rules.requireReasoningAdvance === true,
      relationshipCentered: rules.relationshipCentered === true,
      avoidAmplification: rules.avoidAmplification === true,
    },
    // Used only when every candidate sentence is inadmissible and no
    // regeneration service is connected. A future API-backed execution path
    // may choose REGENERATE instead and request a fresh candidate.
    fallbackResponse: String(input.fallbackResponse || '').trim(),
  };
}

export function validateResponsePolicyContract(policy = null) {
  const errors = [];

  if (!policy || typeof policy !== 'object') {
    return { valid: false, errors: ['Policy must be an object.'] };
  }

  if (policy.schemaVersion !== RESPONSE_POLICY_SCHEMA_VERSION) {
    errors.push(`Unsupported schemaVersion: ${policy.schemaVersion || 'missing'}.`);
  }
  if (!policy.policyId) errors.push('policyId is required.');
  if (!policy.source) errors.push('source is required.');
  if (!policy.authority) errors.push('authority is required.');
  if (!policy.scope) errors.push('scope is required.');
  if (!policy.rules || typeof policy.rules !== 'object') {
    errors.push('rules must be an object.');
  }

  return { valid: errors.length === 0, errors };
}
